import React, { useState, useEffect } from "react";
import DashboardHeader from "../../components/DashboardHeader";
import Sidebar from "../../components/Sidebar";
import "./AccountantReports.css";
import Pagination from "../../components/Pagination";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  getBankStatementsListApi,
  getExpenseListApi,
  getMileageListApi,
  getSalesListApi,
} from "../../services/apiCall";

const AccountantReports = () => {
  const [activeTab, setActiveTab] = useState("sales");
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const businessId = useSelector((state) => state?.user?.businessId);

  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [mileage, setMileage] = useState([]);
  const [bank, setBank] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination State
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, [activeTab, businessId]);

  const fetchData = async () => {
    if (!businessId) return;
    
    try {
      setIsLoading(true);
      let response;
      switch (activeTab) {
        case "sales":
          response = await getSalesListApi({ businessId });
          setSales(response?.data?.sales || []);
          break;
        case "expenses":
          response = await getExpenseListApi({ businessId });
          setExpenses(response?.data?.expenses || []);
          break;
        case "mileage":
          response = await getMileageListApi({ businessId });
          setMileage(response?.data?.mileage || []);
          break;
        case "bank":
          response = await getBankStatementsListApi({ businessId });
          setBank(response?.data?.statements || []);
          break;
        default:
          break;
      }
    } catch (err) {
      toast.error(`Failed to load ${activeTab} data.`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getData = () => {
    switch (activeTab) {
      case "sales": return sales;
      case "expenses": return expenses;
      case "mileage": return mileage;
      case "bank": return bank;
      default: return [];
    }
  };

  const fullData = getData();
  const totalPages = Math.ceil(fullData.length / itemsPerPage);
  const currentData = fullData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const handleView = (item) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };

  const TableHeader = ({ columns }) => (
    <thead>
      <tr>
        {columns.map((col, idx) => (
          <th key={idx} className={col.className || ""}>{col.label}</th>
        ))}
      </tr>
    </thead>
  );

  const NoDataFound = () => (
    <div className="no-data-container">
      <div className="no-data-icon">📂</div>
      <h3>No Records Found</h3>
      <p>It looks like there's no data for {activeTab} at the moment.</p>
    </div>
  );

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading {activeTab} reports...</p>
        </div>
      );
    }

    if (!currentData || currentData.length === 0) {
      return <NoDataFound />;
    }

    let columns = [];
    let rows = [];

    switch (activeTab) {
      case "sales":
        columns = [
          { label: "DOCUMENT" },
          { label: "DATE" },
          { label: "CLIENT" },
          { label: "INVOICE NUMBER" },
          { label: "GROSS" },
          { label: "VAT" },
          { label: "NET" }
        ];
        rows = currentData.map(item => (
          <tr key={item.id}>
            <td><button className="view-btn" onClick={() => handleView(item)}>View</button></td>
            <td>{item.createdAt}</td>
            <td>{item.client || item.invoiceNumber}</td>
            <td>{item.invoiceNumber}</td>
            <td>£{item.gross}</td>
            <td>£{item.vat}</td>
            <td>£{item.net}</td>
          </tr>
        ));
        break;

      case "expenses":
        columns = [
          { label: "DOCUMENT" },
          { label: "DATE" },
          { label: "CATEGORY" },
          { label: "NARRATIVE" },
          { label: "GROSS" },
          { label: "VAT" },
          { label: "NET" },
          { label: "EMPLOYEE" }
        ];
        rows = currentData.map(item => (
          <tr key={item.id}>
            <td><button className="view-btn" onClick={() => handleView(item)}>View</button></td>
            <td>{item.createdAt}</td>
            <td>{item["expenseCategory.name"]}</td>
            <td>{item.description}</td>
            <td>£{item.gross}</td>
            <td>£{item.vat}</td>
            <td>£{item.net}</td>
            <td>{item.employee || "N/A"}</td>
          </tr>
        ));
        break;

      case "mileage":
        columns = [
          { label: "DOCUMENT" },
          { label: "DATE" },
          { label: "CLIENT" },
          { label: "REASON" },
          { label: "FROM" },
          { label: "TO" },
          { label: "MILES" },
          { label: "AMOUNT" },
          { label: "EMPLOYEE" }
        ];
        rows = currentData.map(item => (
          <tr key={item.id}>
            <td><button className="view-btn" onClick={() => handleView(item)}>View</button></td>
            <td>{item.createdAt}</td>
            <td>{item.client}</td>
            <td>{item.reason}</td>
            <td>{item.from}</td>
            <td>{item.to}</td>
            <td>{item.miles}</td>
            <td>£{item.amount}</td>
            <td>{item.employee}</td>
          </tr>
        ));
        break;

      case "bank":
        columns = [
          { label: "DOCUMENT" },
          { label: "DATE" },
          { label: "CATEGORY" },
          { label: "NARRATIVE" },
          { label: "AMOUNT" }
        ];
        rows = currentData.map(item => (
          <tr key={item.id}>
            <td><button className="view-btn" onClick={() => handleView(item)}>View</button></td>
            <td>{item.createdAt}</td>
            <td>{item.transactionType}</td>
            <td>{item.description}</td>
            <td style={{ color: item.amount.toString().startsWith("-") ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
              £{Math.abs(item.amount).toFixed(2)}
            </td>
          </tr>
        ));
        break;
      default:
        return null;
    }

    return (
      <table className="entries-table">
        <TableHeader columns={columns} />
        <tbody>{rows}</tbody>
      </table>
    );
  };

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        <DashboardHeader title="Reports" />
        <div className="entries-container">
          <div className="tabs">
            {["sales", "expenses", "mileage", "bank"].map(tab => (
              <button
                key={tab}
                className={`tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  setPage(1);
                }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          <div className="table-container">
            {renderTableContent()}
            {fullData.length > 0 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                totalItems={fullData.length}
                itemsPerPage={itemsPerPage}
                onPageSizeChange={(newSize) => {
                  setItemsPerPage(newSize);
                  setPage(1);
                }}
              />
            )}
          </div>
        </div>
      </main>

      {/* VIEW MODAL */}
      {isViewModalOpen && selectedItem && (
        <div className="modal-overlays" onClick={() => setIsViewModalOpen(false)}>
          <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <h2>
                {activeTab === "sales" && "Sales Invoice"}
                {activeTab === "expenses" && "Expense Receipt"}
                {activeTab === "mileage" && "Mileage Claim"}
                {activeTab === "bank" && "Bank Transaction"}
              </h2>
              <div className="preview-actions">
                <button onClick={() => window.print()} className="download-btn">Download PDF</button>
                <button className="close-btn" onClick={() => setIsViewModalOpen(false)}>×</button>
              </div>
            </div>

            <div className="invoice-paper">
              <div className="company-header">
                <h1>ABC Tech Ltd.</h1>
                <p>123 Business Street<br />London, EC1A 1BB<br />hello@abctech.co.uk</p>
              </div>

              <div className="invoice-details-grid">
                {activeTab === "sales" && (
                  <>
                    <div><strong>Invoice To:</strong><p>{selectedItem.client || "Client Name"}</p></div>
                    <div><strong>Invoice No:</strong><p>{selectedItem.invoiceNumber}</p><strong>Date:</strong><p>{selectedItem.createdAt}</p></div>
                  </>
                )}
                {activeTab === "expenses" && (
                  <>
                    <div><strong>Employee:</strong><p>{selectedItem.employee || "N/A"}</p><strong>Category:</strong><p>{selectedItem["expenseCategory.name"]}</p></div>
                    <div><strong>Date:</strong><p>{selectedItem.createdAt}</p><strong>Description:</strong><p>{selectedItem.description}</p></div>
                  </>
                )}
                {activeTab === "mileage" && (
                  <>
                    <div><strong>Employee:</strong><p>{selectedItem.employee}</p><strong>Client:</strong><p>{selectedItem.client}</p></div>
                    <div><strong>Date:</strong><p>{selectedItem.createdAt}</p><strong>Route:</strong><p>{selectedItem.from} → {selectedItem.to}</p></div>
                  </>
                )}
                {activeTab === "bank" && (
                  <>
                    <div><strong>Category:</strong><p>{selectedItem.transactionType}</p></div>
                    <div><strong>Date:</strong><p>{selectedItem.createdAt}</p><strong>Description:</strong><p>{selectedItem.description}</p></div>
                  </>
                )}
              </div>

              <table className="amounts-table">
                <tbody>
                  {(activeTab === "sales" || activeTab === "expenses") && (
                    <>
                      <tr><td>Net Amount</td><td className="amount">£{selectedItem.net || "0.00"}</td></tr>
                      <tr><td>VAT</td><td className="amount">£{selectedItem.vat || "0.00"}</td></tr>
                      <tr className="total-row"><td><strong>Total (Gross)</strong></td><td className="amount total"><strong>£{selectedItem.gross || selectedItem.amount}</strong></td></tr>
                    </>
                  )}
                  {activeTab === "mileage" && (
                    <tr className="total-row"><td><strong>Mileage Amount</strong></td><td className="amount total"><strong>£{selectedItem.amount}</strong></td></tr>
                  )}
                  {activeTab === "bank" && (
                    <tr className={selectedItem.amount.toString().startsWith("-") ? "negative" : "positive"}>
                      <td><strong>Amount</strong></td>
                      <td className="amount total"><strong>£{Math.abs(selectedItem.amount).toFixed(2)}</strong></td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="thank-you-note"><p>Thank you for your business!</p></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountantReports;
